import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import BotBuilderService from '../../services/BotBuilderService/BotBuilderService';
import BotStatusContext from './BotStatusContext';
import ChatbotContext from '../ChatbotContext/ChatbotContext';
import LiveBotVersion from '../../types/LiveBotVersion';
import Chatbot from '../../types/Chatbot';
import BuildModalScreen from '../../types/BuildModalScreen';

interface BotStatusProviderProps {
  children: ReactNode;
}

function BotStatusProvider({
  children
}: BotStatusProviderProps): React.ReactElement {
  const { currentChatbot } = useContext(ChatbotContext);
  const [liveBotVersion, setLiveBotVersion] = useState<LiveBotVersion | null>(
    null
  );
  const [isDraftVersion, setIsDraftVersion] = useState<boolean>(false);
  const [previousLiveBotVersion, setPreviousLiveBotVersion] =
    useState<LiveBotVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentScreen, setCurrentScreen] = useState<BuildModalScreen>('build');

  const [progress, setProgress] = useState<number>(0);
  const [hasInitiatedBuild, setHasInitiatedBuild] = useState<boolean>(false);

  const progressRef = useRef<NodeJS.Timeout | null>(null);

  const resetBuildState = useCallback(() => {
    setCurrentScreen('build');
    setHasInitiatedBuild(false);
  }, []);

  useEffect(() => {
    if (currentChatbot) {
      const storedProgress = localStorage.getItem(
        `buildProgress_${currentChatbot.id}`
      );
      if (storedProgress !== null) {
        setProgress(Number(storedProgress));
      }
    }
  }, [currentChatbot]);

  useEffect(() => {
    if (currentChatbot) {
      localStorage.setItem(
        `buildProgress_${currentChatbot.id}`,
        progress.toString()
      );
    }
  }, [progress]);

  const getLiveVersion = (status: Chatbot) => {
    const { liveVersions } = status;
    const liveVersion = liveVersions.reduce(
      (maxObj, currentObj) => (currentObj.id > maxObj.id ? currentObj : maxObj),
      liveVersions[0]
    );
    if (!liveVersion?.buildState) {
      setIsDraftVersion(true);
      return null;
    }
    setIsDraftVersion(false);
    return liveVersion;
  };

  const getChatbotStatus = useCallback(
    (isPollRequest?: boolean) => {
      if (currentChatbot) {
        if (!isPollRequest) setIsLoading(true);
        setError(null);
        BotBuilderService.getChatbot(currentChatbot.id)
          .then((status) => {
            const botVersion = getLiveVersion(status);
            setPreviousLiveBotVersion(liveBotVersion);
            setLiveBotVersion(botVersion);
          })
          .catch((err) => {
            if (err.name === 'CanceledError') return;
            setError(
              err.apiError?.message ||
                'Something went wrong. Please try again later.'
            );
          })
          .finally(() => setIsLoading(false));
      }
    },
    [currentChatbot]
  );

  const buildChatbot = useCallback(() => {
    setProgress(0);
    setIsLoading(true);
    setError('');
    setHasInitiatedBuild(true);

    const tempBuildingVersion: LiveBotVersion = {
      ...liveBotVersion!,
      id: (liveBotVersion?.id || 0) + 1,
      buildState: 'BUILDING',
      creationTime: new Date().toISOString()
    };

    setPreviousLiveBotVersion(liveBotVersion);
    setLiveBotVersion(tempBuildingVersion);
    setIsDraftVersion(false);

    return BotBuilderService.buildChatbot(currentChatbot!.id)
      .then((status) => {
        const botVersion = getLiveVersion(status);
        setPreviousLiveBotVersion(liveBotVersion);
        setLiveBotVersion(botVersion);
        return status;
      })
      .catch((err) => {
        if (err.name === 'CanceledError') throw err;
        setLiveBotVersion(previousLiveBotVersion);
        const errorMessage =
          err.apiError?.message ||
          err.response?.data?.message ||
          'We were currently unable to build a new version of the bot. Please try again later.';
        setError(errorMessage);
        throw new Error(errorMessage);
      })
      .finally(() => setIsLoading(false));
  }, [currentChatbot, liveBotVersion, previousLiveBotVersion]);

  useEffect(() => {
    if (currentChatbot && liveBotVersion) {
      if (liveBotVersion.buildState === 'READY') {
        localStorage.removeItem(`buildProgress_${currentChatbot.id}`);
        setProgress(0);
      }
    }
  }, [currentChatbot, liveBotVersion]);

  useEffect(() => {
    if (liveBotVersion) {
      if (
        liveBotVersion.buildState === 'BUILDING' ||
        liveBotVersion.buildState === 'PARTIALLY_READY' ||
        liveBotVersion.buildState === 'NOT_BUILT'
      ) {
        setCurrentScreen('building');
      } else if (liveBotVersion.buildState === 'READY') {
        if (!hasInitiatedBuild) {
          setCurrentScreen('build');
        } else {
          setProgress(100);
          setCurrentScreen('success');
        }
      } else if (liveBotVersion.buildState === 'BUILD_FAILED') {
        setCurrentScreen('failure');
      } else {
        setCurrentScreen('build');
      }
    } else {
      setCurrentScreen('build');
    }
  }, [liveBotVersion, progress, hasInitiatedBuild]);

  useEffect(() => {
    if (currentScreen === 'building') {
      progressRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          let newProgress: number;
          if (prevProgress < 80) {
            newProgress = prevProgress + Math.random() * 1.5;
          } else if (prevProgress < 95) {
            newProgress = prevProgress + Math.random() * 0.5;
          } else {
            newProgress = prevProgress + Math.random() * 0.1;
          }
          return Math.min(newProgress, 100);
        });
      }, 5000);

      const timeout = setTimeout(
        () => {
          if (progressRef.current) clearInterval(progressRef.current);
        },
        20 * 60 * 1000
      );

      return () => {
        if (progressRef.current) clearInterval(progressRef.current);
        clearTimeout(timeout);
      };
    }
    return () => {};
  }, [currentScreen]);

  useEffect(() => {
    if (currentScreen === 'success' || currentScreen === 'failure') {
      setProgress(100);
      if (progressRef.current) clearInterval(progressRef.current);
    }
  }, [currentScreen]);

  useEffect(() => {
    getChatbotStatus();
    const intervalId = setInterval(() => {
      getChatbotStatus(true);
    }, 30000);
    return () => clearInterval(intervalId);
  }, [currentChatbot, getChatbotStatus]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      error,
      liveBotVersion: liveBotVersion || previousLiveBotVersion,
      getChatbotStatus,
      isDraftVersion,
      buildChatbot,
      currentScreen,
      progress,
      resetBuildState,
      setError
    }),
    [
      isLoading,
      error,
      liveBotVersion,
      previousLiveBotVersion,
      getChatbotStatus,
      isDraftVersion,
      buildChatbot,
      currentScreen,
      progress,
      resetBuildState
    ]
  );

  return (
    <BotStatusContext.Provider value={contextValue}>
      {children}
    </BotStatusContext.Provider>
  );
}

export default BotStatusProvider;
